import { useState, useEffect, Fragment } from 'react'
import { NavLink, useNavigate, useLocation } from 'react-router-dom'
import { X } from 'lucide-react'
import { Icon } from '../assets/icons/Icon'
import { useAuth } from '../lib/auth'
import { useAccount } from '../lib/account'
import { useBranding } from '../lib/branding'
import { useTerminology } from '../lib/terminology'
import { PLAN_ORDER, getRequiredPlan } from '../lib/planGate'
import ProfileModal from './ProfileModal'

/**
 * @typedef {import('./nav-config').NavLink} NavLinkDef
 *
 * @param {{
 *   links: NavLinkDef[],
 *   chatOpen: boolean,
 *   setChatOpen: (v: boolean) => void,
 *   canUseAi: boolean,
 * }} props
 */
export default function MobileNav({ links, chatOpen, setChatOpen, canUseAi }) {
  const { profile, signOut, isOwner } = useAuth()
  const branding = useBranding()
  const { plan } = useAccount()
  const t = useTerminology()
  const navigate = useNavigate()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [showProfile, setShowProfile] = useState(false)
  const location = useLocation()

  // Close drawer on navigation
  useEffect(() => {
    setDrawerOpen(false)
  }, [location.pathname])

  // Bottom nav: show the mobileNav:true items + a "More" button
  const bottomLinks = links.filter(l => l.mobileNav)
  const drawerLinks = links.filter(l => !l.mobileNav)

  return (
    <>
      {/* Mobile header */}
      <header className="md:hidden fixed top-0 left-0 right-0 bg-white border-b border-gray-200 px-4 py-2.5 flex items-center justify-between z-40">
        <div className="flex items-center gap-2">
          <img src={branding.logo_url} alt={branding.org_name} className="h-9 w-auto object-contain" />
          {canUseAi ? (
            <button onClick={() => setChatOpen(!chatOpen)} className={`p-2 rounded-lg transition-colors ${chatOpen ? 'text-brand-700 bg-brand-100' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`} title="AI Assistant">
              <Icon name="chrome-ai-chat" size={20} />
            </button>
          ) : (
            <button onClick={() => navigate('/settings?tab=billing')} className="p-2 rounded-lg transition-colors text-gray-300 hover:bg-gray-100 relative" title="AI Assistant — Upgrade to Pro">
              <Icon name="chrome-ai-chat" size={20} />
              <Icon name="status-locked" size={10} className="absolute top-1 right-1 text-gray-400" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">{profile?.display_name}</span>
          <button onClick={() => setDrawerOpen(true)} className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100">
            <Icon name="chrome-mobile-menu" size={20} />
          </button>
        </div>
      </header>

      {/* Bottom nav bar */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 flex z-40 safe-area-bottom">
        {bottomLinks.map(({ to, icon, label, labelKey, gate }) => {
          const required = getRequiredPlan(gate)
          const locked = required !== null && PLAN_ORDER[plan] < PLAN_ORDER[required]
          const displayLabel = (labelKey && t[labelKey]) || label
          return (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `flex-1 flex flex-col items-center gap-0.5 py-2.5 text-[10px] transition-colors ${locked ? 'opacity-50' : ''} ${
                  isActive ? 'text-brand-700 font-semibold' : 'text-gray-400'
                }`
              }
            >
              <div className="relative">
                <Icon name={icon} size={20} />
                {locked && <Icon name="status-locked" size={10} className="absolute -top-1 -right-1 text-gray-400" />}
              </div>
              {displayLabel}
            </NavLink>
          )
        })}
        {drawerLinks.length > 0 && (
          <button
            onClick={() => setDrawerOpen(true)}
            className={`flex-1 flex flex-col items-center gap-0.5 py-2.5 text-[10px] transition-colors ${drawerOpen ? 'text-brand-700 font-semibold' : 'text-gray-400'}`}
          >
            <Icon name="chrome-mobile-more" size={20} />
            More
          </button>
        )}
      </nav>

      {/* Slide-up drawer for remaining nav items */}
      {drawerOpen && (
        <div className="md:hidden fixed inset-0 z-50">
          {/* Overlay */}
          <div className="absolute inset-0 bg-black/40" onClick={() => setDrawerOpen(false)} />
          {/* Drawer */}
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-xl max-h-[80vh] overflow-y-auto animate-slide-up">
            {/* Handle */}
            <div className="flex justify-center py-2">
              <div className="w-10 h-1 bg-gray-300 rounded-full" />
            </div>
            <div className="px-4 pb-2">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold text-gray-900">More</span>
                <button onClick={() => setDrawerOpen(false)} className="text-gray-400 hover:text-gray-600 p-1"><X size={18} /></button>
              </div>
              <nav className="space-y-1 mb-4">
                {drawerLinks.map(({ to, icon, label, labelKey, gate }, index) => {
                  const required = getRequiredPlan(gate)
                  const locked = required !== null && PLAN_ORDER[plan] < PLAN_ORDER[required]
                  const displayLabel = (labelKey && t[labelKey]) || label
                  const prevGate = drawerLinks[index - 1]?.gate
                  const isFirstPaid = !!gate && !prevGate
                  const isUngatedAfterPaid = !gate && !!prevGate
                  return (
                    <Fragment key={to}>
                      {(isFirstPaid || isUngatedAfterPaid) && (
                        <div className="my-2 mx-1 border-t border-gray-100" />
                      )}
                      <NavLink
                        to={to}
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-3 py-3 text-sm rounded-lg transition-colors ${locked ? 'opacity-50' : ''} ${
                            isActive
                              ? 'bg-brand-100 text-brand-900 font-semibold'
                              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                          }`
                        }
                      >
                        <div className="relative">
                          <Icon name={icon} size={20} />
                          {locked && <Icon name="status-locked" size={10} className="absolute -top-1 -right-1 text-gray-400" />}
                        </div>
                        <span>{displayLabel}</span>
                      </NavLink>
                    </Fragment>
                  )
                })}
              </nav>
              <div className="border-t border-gray-100 pt-3 pb-4 space-y-1">
                <button onClick={() => { setDrawerOpen(false); setShowProfile(true) }} className="flex items-center gap-3 px-3 py-3 text-sm text-gray-600 hover:bg-gray-50 rounded-lg w-full">
                  <Icon name="nav-settings" size={20} />
                  <div className="text-left">
                    <div className="font-medium">{profile?.display_name || 'User'}</div>
                    <div className="text-xs text-gray-400">{isOwner ? 'Owner' : 'Operator'} · Settings</div>
                  </div>
                </button>
                <button onClick={signOut} className="flex items-center gap-3 px-3 py-3 text-sm text-[var(--color-danger)] hover:bg-[var(--color-danger-bg)] rounded-lg w-full">
                  <Icon name="chrome-signout" size={20} />
                  <span>Sign Out</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ProfileModal open={showProfile} onClose={() => setShowProfile(false)} />
    </>
  )
}
